import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, Plus, Phone, Check, Loader2, Trash2, Edit3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import * as profileApi from "../api/profileApi";
import * as locationsApi from "../api/locationsApi";
import SearchableSelect from "../components/SearchableSelect";
import { normalizeEgyptianPhone, validateEgyptianPhone, formatPhoneForDisplay } from "../utils/format";
import "./AccountForms.css";

const emptyForm = {
  country: "Egypt",
  cityId: "",
  districtId: "",
  description: "",
  phoneNumber: "",
  isDefault: false,
};

export default function Addresses() {
  const { userId } = useAuth();
  const { t, isRTL } = useLanguage();
  const { addToast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  const load = async () => {
    try {
      const rows = await profileApi.getAddresses(userId);
      setList(Array.isArray(rows) ? rows : []);
    } catch (e) {
      addToast(t("addresses.loadError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const loadCities = async () => {
    try {
      const res = await locationsApi.getCities();
      setCities(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error("Could not load cities", e);
    }
  };

  useEffect(() => {
    load();
    loadCities();
  }, []);

  useEffect(() => {
    async function loadDistricts() {
      if (!form.cityId) {
        setDistricts([]);
        return;
      }
      try {
        const res = await locationsApi.getDistricts(form.cityId);
        setDistricts(Array.isArray(res) ? res : []);
      } catch (e) {
        console.error("Could not load districts", e);
      }
    }
    loadDistricts();
  }, [form.cityId]);

  const startAdd = () => {
    setEditingId("new");
    setForm(emptyForm);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    let phone = a.phoneNumber || "";
    if (phone.startsWith("+20")) {
      phone = phone.substring(3);
    } else if (phone.startsWith("20") && phone.length === 12) {
      phone = phone.substring(2);
    }
    setForm({
      country: typeof a.country === "string" ? a.country : "Egypt",
      cityId: a.cityId || "",
      districtId: a.districtId || "",
      description: a.description || "",
      phoneNumber: phone,
      isDefault: a.isDefault || false,
    });
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const cancelForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!validateEgyptianPhone(form.phoneNumber)) {
        addToast(
          isRTL
            ? "يرجى إدخال رقم محمول مصري صحيح (مثال: 010xxxxxxxx أو 10xxxxxxxx)."
            : "Please enter a valid Egyptian mobile number (e.g., 010xxxxxxxx or 10xxxxxxxx).",
          "error"
        );
        setSaving(false);
        return;
      }

      const phone = normalizeEgyptianPhone(form.phoneNumber);

      const body = {
        country: form.country,
        cityId: form.cityId,
        districtId: form.districtId,
        description: form.description.trim(),
        phoneNumber: phone,
        isDefault: form.isDefault,
      };

      if (editingId === "new") {
        await profileApi.createAddress(userId, body);
      } else if (editingId) {
        await profileApi.updateAddress(userId, editingId, body);
      }
      cancelForm();
      addToast(t("addresses.saveSuccess"), "success");
      await load();
    } catch (err) {
      addToast(err?.message || "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("addresses.confirmDelete"))) return;
    try {
      await profileApi.deleteAddress(userId, id);
      addToast(t("addresses.deleteSuccess"), "success");
      await load();
    } catch (err) {
      addToast(err?.message || "Could not delete.", "error");
    }
  };

  if (loading) {
    return (
      <div className="form-page">
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      </div>
    );
  }

  return (
    <div className="form-page">
      <div className="container" style={{ maxWidth: 720 }}>
        <Link to="/account" className="form-back btn-ghost">
          <ArrowLeft size={15} className="rtl-flip" />
          <span>{t("addresses.back")}</span>
        </Link>
        <p className="section-label">{t("addresses.sectionLabel")}</p>
        <h1 className="form-page__title">{t("addresses.title")}</h1>

        <div className="address-list">
          {list.length === 0 && !editingId && (
            <div className="address-empty-card">
              <MapPin size={32} className="address-empty-icon" />
              <p>{t("addresses.empty")}</p>
            </div>
          )}

          {list.map((a) => (
            <div key={a.id} className="address-card">
              <div className="address-card__meta">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <strong style={{ color: "var(--text-primary)", fontSize: 15 }}>
                    {a.districtName}, {a.cityName}
                  </strong>
                  {a.isDefault && (
                    <span className="address-default-badge">
                      <Check size={11} />
                      <span>{t("addresses.defaultBadge")}</span>
                    </span>
                  )}
                </div>
                <p style={{ color: "var(--text-secondary)", marginBottom: 4 }}>{a.description}</p>
                {a.phoneNumber && (
                  <p style={{ color: "var(--text-muted)", fontSize: 13, display: "flex", alignItems: "center", gap: 5 }}>
                    <Phone size={12} />
                    <span dir="ltr">{formatPhoneForDisplay(a.phoneNumber)}</span>
                  </p>
                )}
              </div>
              <div className="address-card__actions">
                <button type="button" className="btn-ghost" onClick={() => startEdit(a)}>
                  <Edit3 size={13} />
                  <span>{t("addresses.edit")}</span>
                </button>
                <button type="button" className="btn-ghost address-btn--delete" onClick={() => remove(a.id)}>
                  <Trash2 size={13} />
                  <span>{t("addresses.remove")}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {!editingId && (
          <button type="button" className="btn-outline address-add-btn" onClick={startAdd}>
            <Plus size={15} />
            <span>{t("addresses.add")}</span>
          </button>
        )}

        {editingId && (
          <form ref={formRef} className="account-form" onSubmit={submit} style={{ marginTop: 24 }}>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 500, fontSize: "1.35rem", marginBottom: 20 }}>
              {editingId === "new" ? t("addresses.newTitle") : t("addresses.editTitle")}
            </h3>

            <div className="auth-field">
              <label>{t("addresses.country")}</label>
              <select
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              >
                <option value="Egypt">{t("addresses.egypt")}</option>
              </select>
            </div>

            <div className="form-two-col">
              <div className="auth-field">
                <label>{t("addresses.city")}</label>
                <select
                  value={form.cityId}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, cityId: e.target.value, districtId: "" }));
                  }}
                  required
                >
                  <option value="" disabled>
                    {t("addresses.selectCity")}
                  </option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="auth-field">
                <label>{t("addresses.district")}</label>
                <SearchableSelect
                  options={districts}
                  value={form.districtId}
                  onChange={(val) => setForm((f) => ({ ...f, districtId: val }))}
                  placeholder={t("addresses.selectDistrict")}
                  disabled={!form.cityId || districts.length === 0}
                />
              </div>
            </div>

            <div className="auth-field">
              <label>{t("addresses.description")}</label>
              <input
                value={form.description}
                placeholder={t("addresses.descriptionPlaceholder")}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                required
              />
            </div>

            <div className="auth-field">
              <label>{t("addresses.phone")}</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8, direction: "ltr" }}>
                <div
                  style={{
                    background: "var(--surface-input)",
                    border: "1px solid var(--border)",
                    padding: "0 12px",
                    height: "44px",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "var(--radius)",
                    color: "var(--text-secondary)",
                    fontWeight: "500",
                    fontSize: 13,
                  }}
                >
                  +20
                </div>
                <input
                  value={form.phoneNumber}
                  onChange={(e) => setForm((f) => ({ ...f, phoneNumber: e.target.value }))}
                  required
                  style={{ flex: 1, direction: "ltr", textAlign: "left" }}
                  placeholder={t("addresses.phonePlaceholder")}
                />
              </div>
            </div>

            <label className="auth-field" style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
              />
              <span style={{ fontSize: 14, color: "var(--text-primary)" }}>{t("addresses.setDefault")}</span>
            </label>

            <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 size={15} className="spin-animate" />
                    <span>{t("addresses.saving")}</span>
                  </>
                ) : (
                  t("addresses.save")
                )}
              </button>
              <button type="button" className="btn-outline" onClick={cancelForm}>
                {t("addresses.cancel")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
